/* eslint-disable @typescript-eslint/no-empty-function */
import { useOrgProjectLocalStorage } from '@/hooks/useOrgProjectLocalStorage';
import { useAnalytics } from '@/utils/analytics';
import { getQueryParams } from '@/utils/api-helpers';
import { navRoutes, QueryParams } from '@/utils/routes';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { UrlObject } from 'url';

export type NavigationParams = {
  isOpen: boolean;
  toggleOpen: () => void;
  projectName: string | undefined;
  setProjectName: (_newProjectName: string | undefined) => void;
  organizationName: string | undefined;
  setOrganizationName: (_newOrganizationName: string | undefined) => void;
  openedParentIds: Set<string>;
  toggleParent: (_parentId: string) => void;
  lastProjectUrl: UrlObject | undefined;
  setPageTitle: (_newPageTitle: string) => void;
};

const NavigationParamsContext = createContext<NavigationParams>({
  isOpen: true,
  toggleOpen: () => {},
  projectName: '',
  setProjectName: (_newProjectName: string | undefined) => {},
  organizationName: '',
  setOrganizationName: (_newOrganizationName: string | undefined) => {},
  openedParentIds: new Set<string>(),
  toggleParent: (_parentId: string) => {},
  lastProjectUrl: undefined,
  setPageTitle: (_newPageTitle: string) => {},
});

export function NavigationParamsContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const analytics = useAnalytics();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(true);
  const [openedParentIds, setOpenedParentIds] = useState<Set<string>>(
    new Set<string>(),
  );
  const [projectName, setProjectName] = useState<string>();
  const [organizationName, setOrganizationName] = useState<string>();
  const [lastProjectUrl, setLastProjectUrl] = useState<UrlObject>();
  const [pageTitle, setPageTitle] = useState<string>();
  const { saveOrgId, saveProjectId } = useOrgProjectLocalStorage();

  /**
   * Records the last project page visited
   * so that when in management, clicking on "Back to Project"
   * returns the user to the last page before navigating to management
   */
  useEffect(() => {
    if (
      router.pathname.includes(navRoutes.organizations.pathname) ||
      router.pathname.includes(navRoutes.organization.pathname)
    ) {
      setLastProjectUrl({ pathname: router.pathname, query: router.query });
    }
    const orgId = getQueryParams(router, QueryParams.orgId);
    // reset org or project selected if navigating outside of their
    // respective scopes
    // save to localStorage otherwise
    if (!orgId) {
      setOrganizationName(undefined);
    } else {
      saveOrgId(orgId);
    }
    const projId = getQueryParams(router, QueryParams.projId);
    if (!projId) {
      setProjectName(undefined);
    } else {
      saveProjectId(orgId, projId);
    }
  }, [router.pathname, router.query]);

  function toggleOpen() {
    setIsOpen(!isOpen);
  }

  function handleProjectNameChange(newProjectName: string | undefined) {
    setProjectName(newProjectName);
  }

  function handleOrganizationChange(newOrganizationName: string | undefined) {
    setOrganizationName(newOrganizationName);
  }

  function toggleParent(parentId: string) {
    if (openedParentIds.has(parentId)) {
      setOpenedParentIds(openedParentIds => {
        openedParentIds.delete(parentId);
        return new Set(openedParentIds);
      });
    } else {
      setOpenedParentIds(
        openedParentIds => new Set(openedParentIds.add(parentId)),
      );
    }
  }

  const navigationContext: NavigationParams = {
    isOpen: isOpen,
    toggleOpen: toggleOpen,
    projectName,
    setProjectName: handleProjectNameChange,
    organizationName,
    setOrganizationName: handleOrganizationChange,
    toggleParent: toggleParent,
    openedParentIds: openedParentIds,
    lastProjectUrl,
    setPageTitle,
  };

  useEffect(() => {
    if (pageTitle) {
      const queryParams = Object.keys(router.query).reduce(
        (acc: Record<string, unknown>, key) => {
          switch (key) {
            case 'pid':
              acc.project = router.query[key];
              break;
            case 'orgId':
              acc.organization = router.query[key];
              break;
            default:
              acc[key] = router.query[key];
              break;
          }
          return acc;
        },
        {},
      );

      analytics.page(pageTitle, queryParams);
    }
  }, [pageTitle]);

  return (
    <NavigationParamsContext.Provider value={navigationContext}>
      <Head>
        {/* title needs to be a JS string for now to avoid showing an HTML comment inline, https://github.com/formbio/colossal/pull/1141 */}
        <title>{pageTitle ? `${pageTitle} - FormBio` : 'FormBio'}</title>
        <meta name='viewport' content='initial-scale=1, width=device-width' />
        <link rel='shortcut icon' href='/favicon.ico' />
      </Head>
      {children}
    </NavigationParamsContext.Provider>
  );
}

export const useNavigationContext = () => {
  return useContext(NavigationParamsContext);
};

export default NavigationParamsContext;
